import React, { useCallback, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';
import cnames from 'classnames';
import { defineMessages } from 'react-intl';
import BannerDividerIcon from '../../../Icons/BannerDivider';
import * as constants from '../../../constants';
import styles from './PromoBanner.scss';
import Text from '../../../Text';
import CountdownTimer from './Countdown/CountdownTimer/CountdownTimer';

const SITEWIDE_BANNER = 'sitewide_banner';

const endsInMessage = defineMessages({
  bannerWithEndsIn: '{message} ends in:',
  endsIn: 'ends in:',
  endsSoon: '{message} ends soon',
});

const getBannerMessage = (
  bannerMessageTrimmed,
  initialSeconds,
  showCountdown,
  isDesktop,
  intl,
) => {
  if (showCountdown) {
    if (initialSeconds > 0) {
      if (isDesktop) {
        return intl.formatMessage(endsInMessage.bannerWithEndsIn, {
          message: bannerMessageTrimmed,
        });
      }
      return bannerMessageTrimmed;
    }
    return intl.formatMessage(endsInMessage.endsSoon, {
      message: bannerMessageTrimmed,
    });
  }
  return bannerMessageTrimmed;
};

const PromoBanner = ({
  banners,
  profile,
  initialSeconds,
  intl,
}) => {
  const [inViewRef, inView] = useInView({ triggerOnce: true });
  const containerRef = useRef(null);
  const [banner] = banners || [];
  const bannerMessagee = banner && banner.message;
  const showCountdown = bannerMessagee && bannerMessagee.includes('[c]');
  const bannerMessageTrimmed = bannerMessagee && bannerMessagee.replace('[c]', '').trim();

  const isDesktop = profile === constants.DESKTOP;

  const combinedRefs = useCallback(
    (node) => {
      containerRef.current = node;
      inViewRef(node);
    },
    [inView],
  );

  React.useEffect(() => {
    if (inView && banner && banner.message) {
      window.dataLayer = window.dataLayer || [];

      window.dataLayer.push({
        event: 'view_promotion',
        page_location: window.location.href,
        ecommerce: {
          promotion_name: banner.message,
          creative_slot: SITEWIDE_BANNER,
        },
      });
    }
  }, [inView]);

  const logEvent = () => {
    try {
      window.dataLayer.push({
        event: 'select_promotion',
        page_location: window.location.href,
        ecommerce: {
          promotion_name: banner && banner.message,
          creative_slot: SITEWIDE_BANNER,
        },
      });
    } catch (err) {
      console.error('There was error reporting select_promotion event', err);
    }
  };

  React.useEffect(() => {
    const container = containerRef.current;
    const handleClick = (event) => {
      const link = event.target.closest('a');
      if (link && container && container.contains(link)) {
        logEvent();
      }
    };

    if (banner && banner.message && container) {
      container.addEventListener('click', handleClick);
    }

    return () => {
      if (banner && banner.message && container) {
        container.removeEventListener('click', handleClick);
      }
    };
  }, [banner && banner.message]);

  if (!banners.length) {
    return null;
  }

  const bannerMessage = getBannerMessage(
    bannerMessageTrimmed,
    initialSeconds,
    showCountdown,
    isDesktop,
    intl,
  );

  return (
    <div
      className={cnames(styles.promoBannerContainer, {
        [styles.showCountdown]: !isDesktop && initialSeconds > 0,
        [styles.colourfulBanner]: !initialSeconds,
      })}
      ref={combinedRefs}
    >
      <span
        className={cnames(styles.countdownBannerContainer, {
          [styles.desktop]: isDesktop,
        })}
      >
        {isDesktop && (
          <span className={styles.countdownDividerLeft}>
            <BannerDividerIcon color="#E7F8EF" />
          </span>
        )}
        <span className={cnames(styles.bannerMessage, { [styles.countdownText]: showCountdown })}>
          <Text type="body2">
            <span
              role="button"
              tabIndex={0}
              dangerouslySetInnerHTML={{
                __html: bannerMessage,
              }}
            />
          </Text>
        </span>
        {showCountdown && initialSeconds > 0 && (
          <span className={styles.countdownTimerContainer}>
            {!isDesktop && (
              <span className={styles.countdownText}>
                <Text type="body2">
                  {intl.formatMessage(endsInMessage.endsIn)}
                </Text>
              </span>
            )}
            <CountdownTimer initialSeconds={initialSeconds} />
          </span>
        )}
        {isDesktop && (
          <span className={styles.countdownDividerRight}>
            <BannerDividerIcon color="#E7F8EF" />
          </span>
        )}
      </span>
    </div>
  );
};

PromoBanner.propTypes = {
  banners: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string,
    }),
  ),
};

PromoBanner.defaultProps = {
  banners: [],
};

export default PromoBanner;
